import { ConsumableSelection } from "@/components/ConsumableModal/ConsumableModal";
import UsageForm from "@/components/UsageForm/UsageForm";
import useAnalytics from "@/hooks/useAnalytics";
import useIntercom from "@/hooks/useIntercom";
import useTranslation from "@/hooks/useTranslation";
import styles from "@/pages/products/usage/UsageModal.module.scss";
import { ProductUsageOverview, useGetUsage, usePostUsage } from "@/pages/products/usage/useUsage";
import { CloseOutlined } from "@ant-design/icons";
import { Button, message } from "antd";
import { useForm } from "antd/es/form/Form";
import React, { useState } from "react";

export interface IUsageForm {
  lifespan: number | null;
  lifespan_notes: string | null;
  utilities_used: boolean | null;
  electricity_amount: number | null;
  electricity_notes: string | null;
  consumables_used: boolean | null;
  consumables: ConsumableSelection[];
  status: "complete" | "incomplete";
}

function UsageModal({
  selectedProducts,
  onSuccess,
  children,
}: {
  selectedProducts: ProductUsageOverview[];
  onSuccess: () => void;
  children: React.ReactElement;
}) {
  const translation = useTranslation();
  const analytics = useAnalytics();
  const [form] = useForm<IUsageForm>();
  const [open, setOpen] = useState(false);
  const { setMarginBottom } = useIntercom();

  const postUsage = usePostUsage();

  const title =
    selectedProducts.length > 1
      ? `Edit usage for ${selectedProducts.length} products`
      : "Edit usage";

  const onOpen = () => {
    setMarginBottom(85);
    analytics.track("Usage Modal Opened");
    setOpen(true);
  };

  const onClose = () => {
    setMarginBottom(0);
    form.setFieldsValue({});
    setOpen(false);
  };

  const onSubmit = () => {
    analytics.track("Usage Submitted From Modal");
    form
      .validateFields()
      .then((values) => {
        const postPayload = {
          ...values,
          electricity_amount: values.electricity_amount || null,
          electricity_notes: values.electricity_notes || null,
          utilities_used: values.utilities_used,
          consumables_used: values.consumables_used,
          lifespan: values.lifespan || null,
          lifespan_notes: values.lifespan_notes || null,
          consumables: values.consumables,
        };

        postUsage
          .mutateAsync({
            productIds: selectedProducts.map((product) => product.id),
            usage: postPayload,
          })
          .then(() => {
            message.success(translation.usage.modal.saveSuccess);
            onSuccess();
          })
          .catch(() => {
            message.success(translation.usage.modal.saveFailed);
          });
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const { data, isFetching: dataIsFetching } = useGetUsage({
    productId: selectedProducts[0].id,
    enabled: selectedProducts.length === 1,
  });

  return (
    <>
      <span onClick={onOpen}>{children}</span>
      {open && (
        <div className={styles.UsageModal}>
          <div className={styles.header}>
            {title}
            <CloseOutlined
              className={styles.close}
              onClick={onClose}
            />
          </div>
          <div className={styles.container}>
            <UsageForm
              selectedProducts={selectedProducts}
              form={form}
              onSubmit={onSubmit}
              data={data}
              isLoading={dataIsFetching}
            />
          </div>
          <div className={styles.footer}>
            <span />
            <div>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={form.submit}
                disabled={dataIsFetching || postUsage.isLoading}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UsageModal;
